export interface ZoneCause {
    title: { ru: string; en: string };
    description: { ru: string; en: string };
    severity: 'common' | 'moderate' | 'serious';
}

export interface ZoneRemedy {
    title: { ru: string; en: string };
    description: { ru: string; en: string };
    type: 'selfcare' | 'medication' | 'professional';
}

export interface Zone {
    id: string;
    name: { ru: string; en: string };
    position: { x: number; y: number; z: number };
    radius: number;
    description: { ru: string; en: string };
    causes: ZoneCause[];
    remedies: ZoneRemedy[];
    whenToSeeDoctor: { ru: string; en: string };
}

export const ZONES: Zone[] = [
    {
        id: 'forehead',
        name: { ru: 'Лоб', en: 'Forehead' },
        position: { x: 0, y: 0.25, z: 0.65 },
        radius: 0.3,
        description: {
            ru: 'Боль в области лба — одна из самых распространённых форм головной боли. Может ощущаться как давление, тяжесть или пульсация.',
            en: 'Forehead pain is one of the most common types of headache. It can feel like pressure, heaviness, or pulsation.',
        },
        causes: [
            {
                title: { ru: 'Головная боль напряжения', en: 'Tension headache' },
                description: {
                    ru: 'Самая частая причина. Ощущается как сдавливающий обруч вокруг головы. Возникает от стресса, усталости, недосыпа, длительного напряжения мышц шеи и плеч.',
                    en: 'The most common cause. Feels like a tightening band around the head. Caused by stress, fatigue, lack of sleep, and prolonged neck and shoulder muscle tension.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Фронтальный синусит', en: 'Frontal sinusitis' },
                description: {
                    ru: 'Воспаление лобных пазух. Боль усиливается при наклоне вперёд, сопровождается заложенностью носа и иногда температурой.',
                    en: 'Inflammation of the frontal sinuses. Pain worsens when bending forward, accompanied by nasal congestion and sometimes fever.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Мигрень', en: 'Migraine' },
                description: {
                    ru: 'Пульсирующая боль, часто с одной стороны лба. Может сопровождаться тошнотой, светобоязнью и чувствительностью к звукам.',
                    en: 'Throbbing pain, often on one side of the forehead. May be accompanied by nausea, light sensitivity, and sound sensitivity.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Зрительное перенапряжение', en: 'Eye strain' },
                description: {
                    ru: 'Длительная работа за компьютером, чтение при плохом освещении или некорректная коррекция зрения.',
                    en: 'Prolonged computer work, reading in poor lighting, or incorrect vision correction.',
                },
                severity: 'common',
            },
        ],
        remedies: [
            {
                title: { ru: 'Отдых и релаксация', en: 'Rest and relaxation' },
                description: {
                    ru: 'Лягте в тихой, тёмной комнате на 20–30 минут. Практикуйте глубокое дыхание.',
                    en: 'Lie down in a quiet, dark room for 20–30 minutes. Practice deep breathing.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Холодный компресс', en: 'Cold compress' },
                description: {
                    ru: 'Приложите холодный компресс ко лбу на 15 минут. Эффективно при мигрени и напряжении.',
                    en: 'Apply a cold compress to the forehead for 15 minutes. Effective for migraines and tension.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Обезболивающие', en: 'Pain relievers' },
                description: {
                    ru: 'Ибупрофен, парацетамол или аспирин. Не принимайте чаще 2–3 раз в неделю.',
                    en: 'Ibuprofen, acetaminophen, or aspirin. Do not take more than 2–3 times per week.',
                },
                type: 'medication',
            },
            {
                title: { ru: 'Перерывы для глаз', en: 'Eye breaks' },
                description: {
                    ru: 'Каждые 20 минут смотрите на объект в 6 метрах в течение 20 секунд (правило 20-20-20).',
                    en: 'Every 20 minutes, look at an object 20 feet away for 20 seconds (20-20-20 rule).',
                },
                type: 'selfcare',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Обратитесь к врачу, если боль сильная и внезапная, сопровождается высокой температурой, нарушением зрения, или не проходит более 3 дней.',
            en: 'See a doctor if the pain is severe and sudden, accompanied by high fever, vision problems, or lasts more than 3 days.',
        },
    },
    {
        id: 'temples',
        name: { ru: 'Виски', en: 'Temples' },
        position: { x: 0.65, y: 0.0, z: 0.1 },
        radius: 0.25,
        description: {
            ru: 'Боль в висках может быть острой, пульсирующей или давящей. Часто связана с сосудистыми и мышечными причинами.',
            en: 'Temple pain can be sharp, throbbing, or pressing. Often related to vascular and muscular causes.',
        },
        causes: [
            {
                title: { ru: 'Мигрень', en: 'Migraine' },
                description: {
                    ru: 'Одна из наиболее частых причин височной боли. Пульсирующая, чаще односторонняя, длится от 4 часов до 3 дней.',
                    en: 'One of the most common causes of temple pain. Throbbing, usually one-sided, lasting 4 hours to 3 days.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Головная боль напряжения', en: 'Tension headache' },
                description: {
                    ru: 'Давящая боль в висках и по бокам головы, как будто голову сжимает обруч.',
                    en: 'Pressing pain in temples and sides of head, as if a band is squeezing the head.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Височный артериит', en: 'Temporal arteritis' },
                description: {
                    ru: 'Воспаление височных артерий. Чаще у людей старше 50 лет. Артерия болезненна при прикосновении. ТРЕБУЕТ СРОЧНОЙ МЕДИЦИНСКОЙ ПОМОЩИ.',
                    en: 'Inflammation of the temporal arteries. More common in people over 50. Artery is tender to touch. REQUIRES URGENT MEDICAL ATTENTION.',
                },
                severity: 'serious',
            },
            {
                title: { ru: 'Дисфункция ВНЧС', en: 'TMJ dysfunction' },
                description: {
                    ru: 'Проблемы с височно-нижнечелюстным суставом от бруксизма (скрежетания зубами) или неправильного прикуса.',
                    en: 'Problems with the temporomandibular joint from bruxism (teeth grinding) or malocclusion.',
                },
                severity: 'moderate',
            },
        ],
        remedies: [
            {
                title: { ru: 'Массаж висков', en: 'Temple massage' },
                description: {
                    ru: 'Мягкими круговыми движениями массируйте виски 2–3 минуты. Можно использовать мятное масло.',
                    en: 'Gently massage temples in circular motions for 2–3 minutes. Peppermint oil may help.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Тёплый компресс', en: 'Warm compress' },
                description: {
                    ru: 'Приложите тёплую ткань к вискам при боли напряжения. Расслабляет мышцы.',
                    en: 'Apply a warm cloth to temples for tension pain. Relaxes muscles.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Обезболивающие', en: 'Pain relievers' },
                description: {
                    ru: 'Ибупрофен или парацетамол при эпизодической боли. Триптаны — при диагностированной мигрени (по назначению врача).',
                    en: 'Ibuprofen or acetaminophen for occasional pain. Triptans for diagnosed migraines (doctor-prescribed).',
                },
                type: 'medication',
            },
            {
                title: { ru: 'Снижение стресса', en: 'Stress reduction' },
                description: {
                    ru: 'Медитация, йога, дыхательные упражнения помогают при частых головных болях напряжения.',
                    en: 'Meditation, yoga, and breathing exercises help with frequent tension headaches.',
                },
                type: 'selfcare',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Немедленно обратитесь к врачу, если височная артерия болезненна при прикосновении, есть нарушения зрения, или боль сопровождается болью при жевании — это может быть височный артериит.',
            en: 'Seek immediate medical attention if the temporal artery is tender to touch, you have vision problems, or pain occurs while chewing — this may be temporal arteritis.',
        },
    },
    {
        id: 'top',
        name: { ru: 'Макушка', en: 'Top of head' },
        position: { x: 0, y: 0.95, z: -0.05 },
        radius: 0.3,
        description: {
            ru: 'Боль в верхней части головы ощущается как давление или стягивание сверху. Может быть связана с напряжением мышц или проблемами со сном.',
            en: 'Pain at the top of the head feels like pressure or tightening from above. May be related to muscle tension or sleep problems.',
        },
        causes: [
            {
                title: { ru: 'Головная боль напряжения', en: 'Tension headache' },
                description: {
                    ru: 'Наиболее частая причина боли на макушке. Ощущение «шлема» или давящего обруча сверху.',
                    en: 'Most common cause of vertex pain. Feels like a helmet or pressing band from above.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Недосыпание', en: 'Sleep deprivation' },
                description: {
                    ru: 'Хронический недостаток сна или апноэ во сне могут вызывать боль в верхней части головы.',
                    en: 'Chronic lack of sleep or sleep apnea can cause pain at the top of the head.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Ледяная (колющая) головная боль', en: 'Ice-pick headache' },
                description: {
                    ru: 'Внезапные, острые, кратковременные уколы боли на макушке, длящиеся секунды.',
                    en: 'Sudden, sharp, brief stabs of pain at the top of the head, lasting seconds.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Затылочная невралгия', en: 'Occipital neuralgia' },
                description: {
                    ru: 'Защемление затылочных нервов может вызывать боль, распространяющуюся к макушке.',
                    en: 'Pinched occipital nerves can cause pain radiating to the top of the head.',
                },
                severity: 'moderate',
            },
        ],
        remedies: [
            {
                title: { ru: 'Улучшение осанки', en: 'Posture improvement' },
                description: {
                    ru: 'Держите голову прямо, настройте рабочее место эргономически. Делайте перерывы каждые 30–45 минут.',
                    en: 'Keep your head straight, set up your workspace ergonomically. Take breaks every 30–45 minutes.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Нормализация сна', en: 'Sleep normalization' },
                description: {
                    ru: 'Спите 7–9 часов. Ложитесь и вставайте в одно время. Избегайте экранов за час до сна.',
                    en: 'Sleep 7–9 hours. Go to bed and wake up at the same time. Avoid screens an hour before bed.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Тёплый компресс', en: 'Warm compress' },
                description: {
                    ru: 'Тёплая ткань на макушке и заднюю часть шеи расслабляет напряжённые мышцы.',
                    en: 'A warm cloth on the top of the head and back of the neck relaxes tense muscles.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Обезболивающие', en: 'Pain relievers' },
                description: {
                    ru: 'Парацетамол или ибупрофен при эпизодической боли.',
                    en: 'Acetaminophen or ibuprofen for occasional pain.',
                },
                type: 'medication',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Обратитесь к врачу, если боль на макушке постоянная, усиливается, сопровождается онемением или покалыванием, или возникает после травмы головы.',
            en: 'See a doctor if the pain is persistent, worsening, accompanied by numbness or tingling, or occurs after a head injury.',
        },
    },
    {
        id: 'back',
        name: { ru: 'Затылок', en: 'Back of head' },
        position: { x: 0, y: 0.25, z: -0.75 },
        radius: 0.3,
        description: {
            ru: 'Боль в затылке часто связана с проблемами шейного отдела позвоночника, напряжением мышц шеи или невралгией.',
            en: 'Pain at the back of the head is often related to cervical spine problems, neck muscle tension, or neuralgia.',
        },
        causes: [
            {
                title: { ru: 'Затылочная невралгия', en: 'Occipital neuralgia' },
                description: {
                    ru: 'Воспаление или защемление затылочных нервов. Резкая, стреляющая боль от основания черепа вверх. Может быть болезненно касаться кожи головы.',
                    en: 'Inflammation or pinching of occipital nerves. Sharp, shooting pain from the base of the skull upward. Scalp may be tender to touch.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Цервикогенная головная боль', en: 'Cervicogenic headache' },
                description: {
                    ru: 'Боль исходит из шейного отдела позвоночника — от остеохондроза, грыжи диска, артрита. Обычно односторонняя.',
                    en: 'Pain originates from the cervical spine — from osteochondrosis, disc herniation, or arthritis. Usually one-sided.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Головная боль напряжения', en: 'Tension headache' },
                description: {
                    ru: 'Напряжение мышц шеи и верхней части спины, часто от долгого сидения и «позы смартфона».',
                    en: 'Tension in neck and upper back muscles, often from prolonged sitting and \'smartphone posture\'.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Менингит', en: 'Meningitis' },
                description: {
                    ru: 'Боль в затылке с высокой температурой, ригидностью шеи и светобоязнью. ТРЕБУЕТ НЕМЕДЛЕННОЙ МЕДИЦИНСКОЙ ПОМОЩИ.',
                    en: 'Pain at the back of the head with high fever, stiff neck, and light sensitivity. REQUIRES IMMEDIATE MEDICAL ATTENTION.',
                },
                severity: 'serious',
            },
        ],
        remedies: [
            {
                title: { ru: 'Растяжка шеи', en: 'Neck stretching' },
                description: {
                    ru: 'Медленные наклоны головы вперёд, назад и в стороны. Удерживайте каждое положение 15–30 секунд.',
                    en: 'Slow head tilts forward, backward, and to the sides. Hold each position for 15–30 seconds.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Тепловая терапия', en: 'Heat therapy' },
                description: {
                    ru: 'Тёплый компресс или грелка на шею и затылок на 15–20 минут.',
                    en: 'Warm compress or heating pad on neck and back of head for 15–20 minutes.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Миорелаксанты', en: 'Muscle relaxants' },
                description: {
                    ru: 'При хроническом мышечном напряжении врач может назначить миорелаксанты (тизанидин и др.).',
                    en: 'For chronic muscle tension, a doctor may prescribe muscle relaxants (tizanidine, etc.).',
                },
                type: 'medication',
            },
            {
                title: { ru: 'Физиотерапия', en: 'Physical therapy' },
                description: {
                    ru: 'Специалист поможет укрепить мышцы шеи и исправить осанку. Эффективно при цервикогенной боли.',
                    en: 'A specialist will help strengthen neck muscles and correct posture. Effective for cervicogenic pain.',
                },
                type: 'professional',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Срочно обратитесь к врачу при сочетании боли в затылке с высокой температурой, ригидностью шеи, спутанностью сознания или сыпью — это может быть менингит.',
            en: 'Seek urgent medical attention if occipital pain is combined with high fever, stiff neck, confusion, or rash — this may be meningitis.',
        },
    },
    {
        id: 'behind_eyes',
        name: { ru: 'За глазами', en: 'Behind the eyes' },
        position: { x: 0, y: -0.05, z: 0.55 },
        radius: 0.25,
        description: {
            ru: 'Глубокая боль за глазами может быть острой, пульсирующей или давящей. Часто связана с сосудистыми или синусовыми причинами.',
            en: 'Deep pain behind the eyes can be sharp, throbbing, or pressing. Often related to vascular or sinus causes.',
        },
        causes: [
            {
                title: { ru: 'Кластерная головная боль', en: 'Cluster headache' },
                description: {
                    ru: 'Крайне сильная, жгучая или пронизывающая боль за одним глазом. Длится 15 минут — 3 часа, может повторяться до 8 раз в день.',
                    en: 'Extremely intense burning or piercing pain behind one eye. Lasts 15 min to 3 hours, may recur up to 8 times daily.',
                },
                severity: 'serious',
            },
            {
                title: { ru: 'Мигрень', en: 'Migraine' },
                description: {
                    ru: 'Пульсирующая боль, часто за одним глазом, с тошнотой и чувствительностью к свету и звуку.',
                    en: 'Throbbing pain, often behind one eye, with nausea and sensitivity to light and sound.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Синусит', en: 'Sinusitis' },
                description: {
                    ru: 'Воспаление пазух вокруг глаз. Постоянная тупая боль с ощущением давления, заложенностью носа.',
                    en: 'Inflammation of sinuses around the eyes. Constant dull pain with pressure sensation and nasal congestion.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Перенапряжение глаз', en: 'Eye strain' },
                description: {
                    ru: 'Длительная фокусировка на близких объектах (экран, книга) без перерывов.',
                    en: 'Prolonged focusing on close objects (screen, book) without breaks.',
                },
                severity: 'common',
            },
        ],
        remedies: [
            {
                title: { ru: 'Кислородная терапия', en: 'Oxygen therapy' },
                description: {
                    ru: 'При кластерных болях: вдыхание чистого кислорода через маску — один из самых эффективных методов. Требуется назначение врача.',
                    en: 'For cluster headaches: inhaling pure oxygen through a mask — one of the most effective methods. Requires a doctor\'s prescription.',
                },
                type: 'professional',
            },
            {
                title: { ru: 'Холодный компресс на глаза', en: 'Cold compress on eyes' },
                description: {
                    ru: 'Закройте глаза и приложите холодный компресс на 10–15 минут.',
                    en: 'Close your eyes and apply a cold compress for 10–15 minutes.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Деконгестанты', en: 'Decongestants' },
                description: {
                    ru: 'При синусите: спреи с оксиметазолином, промывание носа солевым раствором.',
                    en: 'For sinusitis: oxymetazoline sprays, saline nasal rinse.',
                },
                type: 'medication',
            },
            {
                title: { ru: 'Отдых для глаз', en: 'Eye rest' },
                description: {
                    ru: 'Делайте перерывы каждые 20 минут, используйте правильное освещение, проверьте зрение.',
                    en: 'Take breaks every 20 minutes, use proper lighting, get your vision checked.',
                },
                type: 'selfcare',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Обратитесь к врачу при очень сильной боли за глазом, покраснении глаза, опущении века, слезотечении — это могут быть кластерные головные боли, требующие лечения.',
            en: 'See a doctor for very severe pain behind the eye, eye redness, drooping eyelid, or tearing — these may be cluster headaches requiring treatment.',
        },
    },
    {
        id: 'one_side',
        name: { ru: 'Одна сторона головы', en: 'One side of head' },
        position: { x: 0.7, y: 0.1, z: 0.0 },
        radius: 0.3,
        description: {
            ru: 'Односторонняя головная боль — когда болит только левая или правая половина головы. Характерна для мигрени и кластерных болей.',
            en: 'Unilateral headache — when only the left or right half of the head hurts. Typical for migraines and cluster headaches.',
        },
        causes: [
            {
                title: { ru: 'Мигрень', en: 'Migraine' },
                description: {
                    ru: 'Самая частая причина односторонней боли. Пульсирующая, усиливается при физической активности. Длится 4–72 часа.',
                    en: 'Most common cause of unilateral pain. Throbbing, worsens with physical activity. Lasts 4–72 hours.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Кластерная головная боль', en: 'Cluster headache' },
                description: {
                    ru: 'Строго односторонняя, очень интенсивная боль. Часто с заложенностью носа и слезотечением на стороне боли.',
                    en: 'Strictly one-sided, very intense pain. Often with nasal congestion and tearing on the affected side.',
                },
                severity: 'serious',
            },
            {
                title: { ru: 'Цервикогенная головная боль', en: 'Cervicogenic headache' },
                description: {
                    ru: 'Боль начинается в шее и распространяется на одну сторону головы. Усиливается при движении шеи.',
                    en: 'Pain starts in the neck and spreads to one side of the head. Worsens with neck movement.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Гемикрания континуа', en: 'Hemicrania continua' },
                description: {
                    ru: 'Редкая форма постоянной односторонней боли различной интенсивности. Хорошо поддаётся лечению индометацином.',
                    en: 'A rare form of constant one-sided pain of varying intensity. Responds well to indomethacin.',
                },
                severity: 'moderate',
            },
        ],
        remedies: [
            {
                title: { ru: 'Триптаны', en: 'Triptans' },
                description: {
                    ru: 'Специализированные препараты от мигрени (суматриптан, золмитриптан). Принимать при первых симптомах по назначению врача.',
                    en: 'Specialized migraine medications (sumatriptan, zolmitriptan). Take at first symptoms as prescribed by a doctor.',
                },
                type: 'medication',
            },
            {
                title: { ru: 'Тёмная тихая комната', en: 'Dark quiet room' },
                description: {
                    ru: 'При мигрени: лягте в прохладной, тёмной, тихой комнате. Минимизируйте сенсорные раздражители.',
                    en: 'For migraines: lie down in a cool, dark, quiet room. Minimize sensory stimuli.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Холодный компресс', en: 'Cold compress' },
                description: {
                    ru: 'Приложите лёд или холодный компресс к болезненной стороне на 15 минут.',
                    en: 'Apply ice or cold compress to the painful side for 15 minutes.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Определение триггеров', en: 'Trigger identification' },
                description: {
                    ru: 'Ведите дневник головных болей, чтобы выявить провокаторы: продукты, стресс, недосып, погода.',
                    en: 'Keep a headache diary to identify triggers: foods, stress, lack of sleep, weather.',
                },
                type: 'selfcare',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Обратитесь к врачу, если односторонняя боль очень сильная, возникла впервые, сопровождается слабостью, нарушением речи или зрения.',
            en: 'See a doctor if one-sided pain is very severe, occurs for the first time, or is accompanied by weakness, speech problems, or vision changes.',
        },
    },
    {
        id: 'jaw',
        name: { ru: 'Челюсть / ВНЧС', en: 'Jaw / TMJ' },
        position: { x: 0.5, y: -0.4, z: 0.15 },
        radius: 0.22,
        description: {
            ru: 'Боль в области челюсти и височно-нижнечелюстного сустава (ВНЧС) часто отдаёт в виски, ухо и шею.',
            en: 'Pain in the jaw and temporomandibular joint (TMJ) area often radiates to temples, ear, and neck.',
        },
        causes: [
            {
                title: { ru: 'Дисфункция ВНЧС', en: 'TMJ dysfunction' },
                description: {
                    ru: 'Боль в челюстном суставе от бруксизма (скрежетания зубами), стресса, неправильного прикуса или травмы. Может сопровождаться щёлканьем.',
                    en: 'Jaw joint pain from bruxism (teeth grinding), stress, malocclusion, or injury. May be accompanied by clicking.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Бруксизм', en: 'Bruxism' },
                description: {
                    ru: 'Непроизвольное сжимание и скрежетание зубами, часто во сне. Вызывает напряжение мышц челюсти и головную боль.',
                    en: 'Involuntary clenching and grinding of teeth, often during sleep. Causes jaw muscle tension and headache.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Мышечное напряжение', en: 'Muscle tension' },
                description: {
                    ru: 'Стресс вызывает непроизвольное сжимание челюсти, что приводит к боли в области ВНЧС и висков.',
                    en: 'Stress causes involuntary jaw clenching, leading to pain in the TMJ area and temples.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Стоматологические проблемы', en: 'Dental problems' },
                description: {
                    ru: 'Неправильный прикус, воспаление зубов или дёсен могут вызывать иррадиирующую боль.',
                    en: 'Malocclusion, tooth or gum inflammation can cause radiating pain.',
                },
                severity: 'moderate',
            },
        ],
        remedies: [
            {
                title: { ru: 'Мягкая пища', en: 'Soft foods' },
                description: {
                    ru: 'Временно перейдите на мягкую пищу, избегайте жевательной резинки и твёрдых продуктов.',
                    en: 'Temporarily switch to soft foods, avoid chewing gum and hard foods.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Упражнения для челюсти', en: 'Jaw exercises' },
                description: {
                    ru: 'Плавно открывайте и закрывайте рот, делайте боковые движения. Массируйте жевательные мышцы.',
                    en: 'Gently open and close your mouth, make side-to-side movements. Massage the chewing muscles.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Ночная каппа', en: 'Night guard' },
                description: {
                    ru: 'Стоматолог может изготовить индивидуальную каппу для защиты зубов от бруксизма.',
                    en: 'A dentist can make a custom night guard to protect teeth from bruxism.',
                },
                type: 'professional',
            },
            {
                title: { ru: 'Тёплый компресс', en: 'Warm compress' },
                description: {
                    ru: 'Приложите тёплую ткань к области ВНЧС на 15–20 минут для расслабления мышц.',
                    en: 'Apply a warm cloth to the TMJ area for 15–20 minutes to relax muscles.',
                },
                type: 'selfcare',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Обратитесь к стоматологу или челюстно-лицевому хирургу, если челюсть блокируется, есть постоянный хруст, или боль не проходит более недели.',
            en: 'See a dentist or oral surgeon if your jaw locks, there is constant crunching, or pain persists for more than a week.',
        },
    },
    {
        id: 'neck',
        name: { ru: 'Шея / Основание черепа', en: 'Neck / Skull base' },
        position: { x: 0, y: -0.85, z: -0.3 },
        radius: 0.25,
        description: {
            ru: 'Боль в области шеи и основания черепа часто распространяется вверх на голову. Связана с мышечным напряжением и проблемами позвоночника.',
            en: 'Pain in the neck and skull base area often spreads upward to the head. Related to muscle tension and spinal problems.',
        },
        causes: [
            {
                title: { ru: 'Цервикогенная головная боль', en: 'Cervicogenic headache' },
                description: {
                    ru: 'Боль исходит из шейного отдела: остеохондроз, грыжа диска, артрит шейных позвонков. Часто односторонняя.',
                    en: 'Pain originates from the cervical spine: osteochondrosis, disc herniation, cervical arthritis. Often one-sided.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Мышечное напряжение', en: 'Muscle tension' },
                description: {
                    ru: '«Поза смартфона», длительная работа за компьютером, неудобная подушка. Мышцы шеи перенапрягаются.',
                    en: '\'Smartphone posture\', prolonged computer work, uncomfortable pillow. Neck muscles become overstrained.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Затылочная невралгия', en: 'Occipital neuralgia' },
                description: {
                    ru: 'Защемление нервов у основания черепа. Острая, стреляющая боль от шеи к макушке.',
                    en: 'Pinched nerves at the skull base. Sharp, shooting pain from neck to the top of head.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Последствия травмы', en: 'Injury aftermath' },
                description: {
                    ru: 'Хлыстовая травма или ушиб шеи могут вызывать длительные головные боли.',
                    en: 'Whiplash or neck injury can cause prolonged headaches.',
                },
                severity: 'moderate',
            },
        ],
        remedies: [
            {
                title: { ru: 'Растяжка и укрепление', en: 'Stretching and strengthening' },
                description: {
                    ru: 'Ежедневные упражнения для шеи: наклоны, повороты, упражнения с сопротивлением. Укрепляют глубокие мышцы.',
                    en: 'Daily neck exercises: tilts, turns, resistance exercises. Strengthen deep muscles.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Эргономика рабочего места', en: 'Workplace ergonomics' },
                description: {
                    ru: 'Монитор на уровне глаз, удобное кресло, перерывы каждые 30 минут.',
                    en: 'Monitor at eye level, comfortable chair, breaks every 30 minutes.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Физиотерапия', en: 'Physical therapy' },
                description: {
                    ru: 'Специалист подберёт программу упражнений, мануальную терапию и при необходимости аппаратные методы.',
                    en: 'A specialist will design an exercise program, manual therapy, and if needed, instrumental methods.',
                },
                type: 'professional',
            },
            {
                title: { ru: 'Обезболивающие и миорелаксанты', en: 'Pain relievers and muscle relaxants' },
                description: {
                    ru: 'НПВС (ибупрофен, напроксен) при обострении. Миорелаксанты при хроническом напряжении — по назначению врача.',
                    en: 'NSAIDs (ibuprofen, naproxen) for flare-ups. Muscle relaxants for chronic tension — as prescribed by a doctor.',
                },
                type: 'medication',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Обратитесь к врачу, если боль в шее сопровождается слабостью в руках, онемением, нарушением координации, или возникла после травмы.',
            en: 'See a doctor if neck pain is accompanied by arm weakness, numbness, coordination problems, or occurred after an injury.',
        },
    },
    {
        id: 'sinuses',
        name: { ru: 'Пазухи (область носа)', en: 'Sinuses (nasal area)' },
        position: { x: 0, y: -0.25, z: 0.7 },
        radius: 0.2,
        description: {
            ru: 'Боль и давление в области пазух — вокруг носа, под глазами и во лбу. Усиливается при наклоне вперёд.',
            en: 'Pain and pressure in the sinus area — around the nose, under the eyes, and in the forehead. Worsens when bending forward.',
        },
        causes: [
            {
                title: { ru: 'Синусит (гайморит)', en: 'Sinusitis' },
                description: {
                    ru: 'Бактериальное или вирусное воспаление пазух. Боль, давление, заложенность носа, иногда гнойные выделения и температура.',
                    en: 'Bacterial or viral sinus inflammation. Pain, pressure, nasal congestion, sometimes purulent discharge and fever.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Аллергический ринит', en: 'Allergic rhinitis' },
                description: {
                    ru: 'Аллергия на пыль, пыльцу, шерсть животных вызывает отёк слизистой и боль в пазухах.',
                    en: 'Allergy to dust, pollen, or pet dander causes mucosal swelling and sinus pain.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Мигрень (имитирующая синусит)', en: 'Migraine (mimicking sinusitis)' },
                description: {
                    ru: 'До 90% «синусовых головных болей» на самом деле оказываются мигренями. Важна правильная диагностика.',
                    en: 'Up to 90% of \'sinus headaches\' actually turn out to be migraines. Proper diagnosis is important.',
                },
                severity: 'moderate',
            },
            {
                title: { ru: 'Искривление перегородки / полипы', en: 'Deviated septum / polyps' },
                description: {
                    ru: 'Анатомические особенности носа могут нарушать отток из пазух и вызывать хроническую боль.',
                    en: 'Nasal anatomical features can impair sinus drainage and cause chronic pain.',
                },
                severity: 'moderate',
            },
        ],
        remedies: [
            {
                title: { ru: 'Промывание носа', en: 'Nasal irrigation' },
                description: {
                    ru: 'Солевой раствор или готовые спреи (Аква Марис и др.) для очищения пазух и разжижения слизи.',
                    en: 'Saline solution or ready-made sprays for cleaning sinuses and thinning mucus.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Паровые ингаляции', en: 'Steam inhalation' },
                description: {
                    ru: 'Дышите над горячей водой 10–15 минут. Можно добавить эвкалиптовое масло.',
                    en: 'Breathe over hot water for 10–15 minutes. Eucalyptus oil may be added.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Деконгестанты', en: 'Decongestants' },
                description: {
                    ru: 'Сосудосуживающие капли (оксиметазолин) — не более 3–5 дней. Антигистаминные при аллергии.',
                    en: 'Vasoconstrictor drops (oxymetazoline) — no more than 3–5 days. Antihistamines for allergies.',
                },
                type: 'medication',
            },
            {
                title: { ru: 'Антибиотики', en: 'Antibiotics' },
                description: {
                    ru: 'При бактериальном синусите врач может назначить курс антибиотиков.',
                    en: 'For bacterial sinusitis, a doctor may prescribe a course of antibiotics.',
                },
                type: 'professional',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Обратитесь к врачу, если симптомы длятся более 10 дней, есть высокая температура, гнойные выделения или боль усиливается.',
            en: 'See a doctor if symptoms last more than 10 days, there is high fever, purulent discharge, or pain is worsening.',
        },
    },
    {
        id: 'crown',
        name: { ru: 'Темя', en: 'Crown' },
        position: { x: 0, y: 0.8, z: 0.45 },
        radius: 0.25,
        description: {
            ru: 'Боль в области темени — передне-верхней части головы. Часто связана с напряжением, обезвоживанием или нарушением сна.',
            en: 'Pain in the crown area — the front-upper part of the head. Often related to tension, dehydration, or sleep disruption.',
        },
        causes: [
            {
                title: { ru: 'Головная боль напряжения', en: 'Tension headache' },
                description: {
                    ru: 'Самая частая причина. Ощущение сдавливания или тяжести в области темени от стресса и мышечного напряжения.',
                    en: 'Most common cause. Feeling of squeezing or heaviness in the crown area from stress and muscle tension.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Обезвоживание', en: 'Dehydration' },
                description: {
                    ru: 'Недостаток жидкости вызывает уменьшение объёма мозга и натяжение оболочек, что проявляется болью сверху.',
                    en: 'Lack of fluids causes brain volume reduction and membrane tension, manifesting as pain from above.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Недосыпание', en: 'Sleep deprivation' },
                description: {
                    ru: 'Хронический дефицит сна — одна из самых частых причин головной боли любой локализации.',
                    en: 'Chronic sleep deficit is one of the most common causes of headache of any location.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Ледяная (колющая) головная боль', en: 'Ice-pick headache' },
                description: {
                    ru: 'Короткие (1–3 секунды), острые уколы боли. Безопасны, но неприятны.',
                    en: 'Short (1–3 seconds), sharp stabs of pain. Harmless but unpleasant.',
                },
                severity: 'common',
            },
        ],
        remedies: [
            {
                title: { ru: 'Гидратация', en: 'Hydration' },
                description: {
                    ru: 'Выпивайте не менее 6–8 стаканов воды в день. При боли выпейте 1–2 стакана воды и подождите 30 минут.',
                    en: 'Drink at least 6–8 glasses of water daily. For pain, drink 1–2 glasses and wait 30 minutes.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Нормализация сна', en: 'Sleep normalization' },
                description: {
                    ru: '7–9 часов сна, регулярный режим, удобная подушка. Избегайте кофеина после 14:00.',
                    en: '7–9 hours of sleep, regular schedule, comfortable pillow. Avoid caffeine after 2 PM.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Управление стрессом', en: 'Stress management' },
                description: {
                    ru: 'Медитация, прогрессивная мышечная релаксация, прогулки на свежем воздухе.',
                    en: 'Meditation, progressive muscle relaxation, outdoor walks.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Обезболивающие', en: 'Pain relievers' },
                description: {
                    ru: 'Парацетамол или ибупрофен при выраженной боли. Не злоупотребляйте — возможна абузусная головная боль.',
                    en: 'Acetaminophen or ibuprofen for pronounced pain. Don\'t overuse — medication overuse headache is possible.',
                },
                type: 'medication',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Обратитесь к врачу, если боль постоянная, нарастающая, появилась после 50 лет впервые, или сопровождается другими неврологическими симптомами.',
            en: 'See a doctor if pain is persistent, worsening, appeared for the first time after age 50, or is accompanied by other neurological symptoms.',
        },
    },
    {
        id: 'whole_head',
        name: { ru: 'Вся голова', en: 'Whole head' },
        position: { x: 0, y: 0.1, z: 0 },
        radius: 0.5,
        description: {
            ru: 'Когда болит вся голова — разлитая, диффузная головная боль без чёткой локализации.',
            en: 'When the whole head hurts — diffuse headache without clear localization.',
        },
        causes: [
            {
                title: { ru: 'Обезвоживание', en: 'Dehydration' },
                description: {
                    ru: 'Самая частая причина диффузной головной боли. Организму не хватает жидкости для нормального функционирования.',
                    en: 'The most common cause of diffuse headache. The body lacks fluids for normal functioning.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Головная боль напряжения', en: 'Tension headache' },
                description: {
                    ru: 'Может охватывать всю голову — ощущение «тисков» или тяжёлого шлема.',
                    en: 'Can encompass the entire head — feeling of a \'vise\' or heavy helmet.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Вирусная инфекция (ОРВИ, грипп)', en: 'Viral infection (cold, flu)' },
                description: {
                    ru: 'Общая головная боль при простуде и гриппе — реакция организма на воспаление.',
                    en: 'General headache during colds and flu — the body\'s response to inflammation.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Отмена кофеина', en: 'Caffeine withdrawal' },
                description: {
                    ru: 'Если вы привыкли к кофе и резко прекратили — головная боль может длиться 2–9 дней.',
                    en: 'If you\'re used to coffee and suddenly stop — headache can last 2–9 days.',
                },
                severity: 'common',
            },
            {
                title: { ru: 'Абузусная головная боль', en: 'Medication overuse headache' },
                description: {
                    ru: 'Парадоксальная боль от чрезмерного приёма обезболивающих (более 15 дней в месяц).',
                    en: 'Paradoxical pain from excessive painkiller use (more than 15 days per month).',
                },
                severity: 'moderate',
            },
        ],
        remedies: [
            {
                title: { ru: 'Обильное питьё', en: 'Plenty of fluids' },
                description: {
                    ru: 'Выпейте 2–3 стакана воды. Поддерживайте гидратацию в течение дня.',
                    en: 'Drink 2–3 glasses of water. Maintain hydration throughout the day.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Отдых', en: 'Rest' },
                description: {
                    ru: 'Отдохните в тихом, затемнённом помещении. При возможности — короткий сон 20–30 минут.',
                    en: 'Rest in a quiet, dimly lit room. If possible, take a short 20–30 minute nap.',
                },
                type: 'selfcare',
            },
            {
                title: { ru: 'Обезболивающие', en: 'Pain relievers' },
                description: {
                    ru: 'Разовый приём ибупрофена или парацетамола. Не принимайте обезболивающие более 2–3 раз в неделю.',
                    en: 'A single dose of ibuprofen or acetaminophen. Don\'t take painkillers more than 2–3 times per week.',
                },
                type: 'medication',
            },
            {
                title: { ru: 'Регулярное питание', en: 'Regular meals' },
                description: {
                    ru: 'Ешьте регулярно, не пропускайте приёмы пищи. Резкие колебания сахара в крови вызывают головную боль.',
                    en: 'Eat regularly, don\'t skip meals. Sharp blood sugar fluctuations cause headaches.',
                },
                type: 'selfcare',
            },
        ],
        whenToSeeDoctor: {
            ru: 'Обратитесь к врачу, если головная боль «самая сильная в жизни», возникла внезапно (громоподобная), сопровождается лихорадкой, ригидностью шеи, спутанностью сознания или судорогами.',
            en: 'See a doctor if the headache is \'the worst of your life\', occurred suddenly (thunderclap), accompanied by fever, stiff neck, confusion, or seizures.',
        },
    },
];
